import { Link } from "react-router-dom";
import {
  Wine as BottleIcon, BookOpen as BookIcon, MessageSquareHeart, Gift,
  ArrowRight, Sparkles, Compass,
} from "lucide-react";

import { _t } from "@/lib/i18n";
/**
 * Homepage "Esplora" — anteprime delle sezioni disponibili in Time Capsule.
 * Ogni card ha: icona, titolo, sottotitolo/status, descrizione breve, CTA.
 * Le funzioni WIP sono marcate esplicitamente e non hanno CTA attiva.
 */
const FEATURES = [
  {
    key: "bacheca",
    to: "/bacheca",
    icon: MessageSquareHeart,
    title: _t("Bacheca dei Ricordi"),
    tag: _t("Community"),
    tagClass: "text-cyan-300 border-cyan-400/30 bg-cyan-500/10",
    description:
      _t("Il feed pubblico delle capsule aperte: ogni volta che una capsula si sblocca, l'autore può condividere frammenti con la community."),
    accent: "from-cyan-500/20 to-cyan-500/0",
    cta: _t("Vai alla Bacheca"),
    active: true,
  },
  {
    key: "miab",
    to: "/miab",
    icon: BottleIcon,
    title: _t("Message in a Bottle"),
    tag: _t("Anonimo · a tempo"),
    tagClass: "text-amber-300 border-amber-400/30 bg-amber-500/10",
    description:
      _t("Lancia un messaggio anonimo che sarà svelato al mondo tra mesi o anni. Ogni MIAB è cifrato, sigillato on-chain e reso pubblico solo alla scadenza."),
    accent: "from-amber-500/20 to-amber-500/0",
    cta: _t("Scopri MIAB"),
    active: true,
  },
  {
    key: "racconti",
    to: "/racconti",
    icon: BookIcon,
    title: _t("Racconti nel Tempo"),
    tag: _t("Autori · Pay-per-Attention"),
    tagClass: "text-fuchsia-300 border-fuchsia-400/30 bg-fuchsia-500/10",
    description:
      _t("Piattaforma editoriale: gli autori sigillano capitoli, li annunciano 14 giorni prima, li rilasciano al pubblico. Ricavo condiviso in base all'attenzione reale."),
    accent: "from-fuchsia-500/20 to-fuchsia-500/0",
    cta: _t("Sfoglia i Racconti"),
    active: true,
  },
  {
    key: "voucher",
    to: "/voucher",
    icon: Gift,
    title: _t("Voucher · valore nel tempo"),
    tag: _t("Coming Soon"),
    tagClass: "text-white/50 border-white/20 bg-white/5",
    description:
      _t("Allega un valore economico alla capsula, custodito in modo sicuro fino alla data di apertura. Il beneficiario lo riscatta solo a scadenza, mai prima."),
    accent: "from-white/10 to-white/0",
    cta: _t("In preparazione"),
    active: false,
  },
];

export default function ExploreSection() {
  return (
    <section className="mb-14" data-testid="explore-section" id="esplora">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-2 flex items-center gap-2">
            <Compass className="w-3.5 h-3.5" />{" "}{_t("Esplora Time Capsule")}</div>
          <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">{_t("Non solo capsule private")}</h2>
          <p className="text-white/60 mt-1 max-w-2xl text-sm">{_t("Time Capsule è anche una piattaforma pubblica: community di ricordi, messaggi al futuro, editoria a tempo. Dai un'occhiata alle sezioni.")}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {FEATURES.map((f) => (
          <FeatureCard key={f.key} f={f} />
        ))}
      </div>
    </section>
  );
}

function FeatureCard({ f }) {
  const Icon = f.icon;
  const CardInner = (
    <div
      data-testid={`explore-card-${f.key}`}
      className={`tc-card p-6 relative overflow-hidden h-full transition-transform group ${
        f.active ? "hover:-translate-y-0.5 cursor-pointer" : "opacity-90 cursor-not-allowed"
      }`}
    >
      <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${f.accent} blur-2xl`} />
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-white/80" />
          </div>
          <span className={`text-[10px] uppercase tracking-[0.2em] border rounded-full px-2 py-[3px] ${f.tagClass}`}>
            {f.tag}
          </span>
        </div>
        <h3 className="font-display text-xl text-white leading-tight mb-2">{f.title}</h3>
        <p className="text-sm text-white/60 leading-relaxed flex-1">{f.description}</p>
        <div className="mt-5 pt-4 border-t border-white/5">
          {f.active ? (
            <div className="text-sm text-[#FF5722] font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              {f.cta} <ArrowRight className="w-3.5 h-3.5" />
            </div>
          ) : (
            <div className="text-sm text-white/40 inline-flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" /> {f.cta}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return f.active ? (
    <Link to={f.to} className="block h-full">{CardInner}</Link>
  ) : (
    <div className="h-full" aria-disabled="true">{CardInner}</div>
  );
}
